import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, NotebookPen } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS } from "./ResultsEditor";
import { KidHome } from "./KidHome";
import { StudentHome } from "./StudentHome";
import { ParentFeedback } from "./ParentFeedback";
import { DiaryHistory } from "./DiaryHistory";

const shift = (d, n) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const pretty = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" });

const TeacherToday = ({ day }) => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-2" data-testid="today-at-ptg">
    <p className="font-bold text-emerald-900">Today at PTG</p>
    {day.plans.length === 0 && day.marks.length === 0 && <p className="text-sm text-slate-500" data-testid="today-empty">The teacher hasn't added today's plan yet.</p>}
    {!day.class_saved && day.plans.map((p) => (
      <div key={p.id} className="text-sm" data-testid={`today-plan-${p.id}`}>
        <div className="flex flex-wrap gap-1">{[...(p.activities || []), ...p.chips].map((c) => <span key={c} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">{c}</span>)}</div>
        {p.topic && <p className="mt-0.5 text-slate-800"><b>Topic:</b> {p.topic}</p>}
        {(p.homework_items?.length ? p.homework_items : p.homework ? [p.homework] : []).length > 0 && <p className="mt-1 rounded-lg bg-white px-2 py-1 text-slate-800 ring-1 ring-amber-200" data-testid={`today-hw-${p.id}`}>📝 <b>Homework:</b> {(p.homework_items?.length ? p.homework_items : [p.homework]).join(" · ")} <span className="text-xs text-slate-500">· due {p.homework_due}</span></p>}
        {p.test && <p className="text-violet-800">🧪 Test: {p.test.name}</p>}
        {p.text && <p className="mt-0.5 text-slate-700">{p.text}</p>}
        <p className="text-xs text-slate-500">{p.teacher_name} · {p.period === "week" ? "this week" : "today"}</p>
      </div>
    ))}
    {day.marks.map((m, i) => {
      const s = STATUS.find((x) => x.k === m.status);
      return (
        <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`today-mark-${i}`}>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${s ? s.cls : "bg-slate-200 text-slate-600"}`}>{s ? s.label : "Absent"}</span>
          <span className="text-slate-600">{m.teacher_name}</span>{m.note && <span className="text-slate-800">“{m.note}”</span>}
          {(m.marks != null || m.grade) && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-800" data-testid={`today-result-${i}`}>Test: {m.grade ? `Grade ${m.grade}` : `${m.marks}${m.out_of ? ` / ${m.out_of}` : ""}`}</span>}
        </div>
      );
    })}
    {day.pending && <p className="rounded-lg bg-red-50 px-2 py-1 text-sm font-semibold text-red-700" data-testid="today-pending">
      {day.pending.status === "retest" ? "🔁 Retest / redo pending" : "⏳ Pending work"} from {day.pending.date}{day.pending.note ? ` — ${day.pending.note}` : ""}</p>}
  </div>
);

export default function StudentDiary() {
  const [date, setDate] = useState(null);
  const [day, setDay] = useState(null);
  const [hist, setHist] = useState(false);
  const load = useCallback((d) => api.get("/student/diary", { params: d ? { d } : {} }).then((r) => { setDay(r.data); setDate(r.data.date); }), []);
  useEffect(() => { load(); }, [load]);
  const go = (n) => { setDay(null); load(shift(date, n)); };
  if (!day) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  return (
    <div className="max-w-2xl space-y-4" data-testid="student-diary">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><NotebookPen className="h-5 w-5 text-emerald-700" /> My Diary</h2>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => go(-1)} className="rounded-full p-2 hover:bg-slate-100" aria-label="Previous day" data-testid="diary-prev"><ChevronLeft className="h-5 w-5" /></button>
          <span className="text-sm font-semibold text-slate-700" data-testid="diary-day-label">{pretty(day.date)}</span>
          <button type="button" onClick={() => go(1)} disabled={day.date >= day.today} className="rounded-full p-2 hover:bg-slate-100 disabled:opacity-30" aria-label="Next day" data-testid="diary-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>
      <button type="button" onClick={() => setHist((v) => !v)} className="rounded-full border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50" data-testid="diary-history-toggle">{hist ? "Back to today" : "History — see any day or dates"}</button>
      {hist ? <DiaryHistory url="/student/diary/history" pdfUrl="/student/diary/history.pdf" photoBase="/student/diary/photo" /> : <>
      <TeacherToday day={day} />
      {day.kid_mode ? <KidHome day={day} onSaved={() => load(day.date)} /> : <StudentHome day={day} onSaved={() => load(day.date)} />}
      {(day.plans.length > 0 || day.marks.length > 0) && <ParentFeedback date={day.date} home={day.home} onSaved={() => load(day.date)} />}
      </>}
    </div>
  );
}
